import { Text } from '@thanx/uikit/text'
import LockCircled from 'assets/images/billing/img_lock-circled.svg'
import LiveUpdatesDisabledBanner from 'components/LiveUpdatesDisabledBanner'
import PageHeader from 'components/PageHeader'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import { useCanOpenLiveUpdates } from 'hooks/useCanOpenLiveUpdates'
import useFlag from 'hooks/useFlag'
import { useLiveUpdatesState } from 'hooks/useLiveUpdatesState'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { I18n } from 'react-redux-i18n'
// Cross-scene imports (same deviation the old combined page carried, see git
// history): the Feedback tab is a RecoveryAI surface — it reads the
// recovery-ai flag, uses api/recoveryAi.types and shares
// components/FeedbackTable — so lifting those to components/ is
// disproportionate here. `FeedbackInbox`/`HostFlexShell`/`useInboxMountState`
// are the same class of exception, documented at their own definitions.
import FeedbackInbox from 'scenes/FeedbackInbox'
import HostFlexShell from 'scenes/FeedbackInbox/HostFlexShell'
import {
  isOrderFeedbackTabVisible,
  useInboxMountState,
} from 'scenes/FeedbackInbox/useInboxMountState'
import { useScrollInboxIntoView } from 'scenes/FeedbackInbox/useScrollInboxIntoView'
import DetailsPane from 'scenes/Reports/OrderFeedbackReport/DetailsPane'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
// Scopes the `.container` max-width override below to this page only (same
// technique as `.gift-card-provider-tab` in stylesheets/_tabs.scss).
import './OrderFeedback.scss'
import Overview from './overview/Overview'
import { resolveOverviewState } from './overview/resolveOverviewState'
import { useOverviewSignals } from './overview/useOverviewSignals'
import SettingsPane from './settings'

const t = buildTranslate('order_feedback')
const liveUpdatesT = buildTranslate('live_updates')
const errorsT = buildTranslate('errors')
const LOCK_ICON_WIDTH_PX = 32

enum Tabs {
  OVERVIEW = 'overview',
  FEEDBACK = 'feedback',
  SETTINGS = 'settings',
}

const isTab = (value: string): value is Tabs =>
  value === Tabs.OVERVIEW || value === Tabs.FEEDBACK || value === Tabs.SETTINGS

// Adopts thanx/thanx-merchant-ui#4106's `split('/')[0]` segment parsing so a
// Settings sub-nav deep link (`#settings/feedback_collection`) resolves to the
// Settings tab the moment that pane mounts, without the two PRs fighting over
// this function's shape. `#details` is a permanent alias for the renamed
// Feedback tab — existing bookmarks and the Reports redirect both carry it.
const readTabFromHash = (hash: string): Tabs | null => {
  const segment = hash.replace('#', '').split('/')[0]
  if (segment === 'details') return Tabs.FEEDBACK
  return isTab(segment) ? segment : null
}

const OrderFeedback = () => {
  const ability = useAbility()
  const liveUpdatesState = useLiveUpdatesState()
  const canOpenLiveUpdates = useCanOpenLiveUpdates()

  // The Feedback tab's CASL gate — unchanged from the pre-split page.
  const canViewDetails = ability.can('access', 'Feedback')
  // Gates /recovery_ai. Without it this tab is the user's only inbox entry,
  // so it stays at full RecoveryAI coverage (AI-1425).
  const canAccessMerchantSettings = ability.can('access', 'MerchantSettings')

  // Unified Feedback Inbox (AI-1320). Flag OFF keeps `DetailsPane`, the
  // legacy table, so this mount is a swap behind one boolean rather than a
  // rewrite of the tab. `setupState`/`collecting` are the inbox's required
  // mount API (docs/05-frontend-components.md), read from recovery/status by
  // `useInboxMountState` (AI-1406).
  const unifiedInboxEnabled = useFlag(
    'recovery-ai-unified-feedback-inbox',
    false
  )
  const inboxMountState = useInboxMountState(unifiedInboxEnabled)
  const { setupState, collecting, writtenFeedbackEnabled } = inboxMountState

  // Business-rule visibility, independent of permission. The design HIDES a
  // tab it has nothing to put behind, so these two drive whether the tab
  // exists at all. While Live Updates is off the tabs that would otherwise
  // show are greyed out instead of hidden (see `isLiveUpdatesDisabled`).
  const { signals } = useOverviewSignals()
  const overviewState = resolveOverviewState(signals)
  const { settingsTabVisible } = overviewState
  // Flag on: the inbox's setup-state rule replaces the Overview resolver's
  // for this one tab (AI-1406); Settings and the Overview content keep the
  // resolver. Flag off: unchanged.
  const feedbackTabVisible = unifiedInboxEnabled
    ? isOrderFeedbackTabVisible({
        ...inboxMountState,
        canAccessFeedback: canViewDetails,
        canAccessMerchantSettings,
      })
    : overviewState.feedbackTabVisible
  const isLiveUpdatesDisabled = liveUpdatesState.isDisabled
  const canViewFeedbackTab =
    canViewDetails && feedbackTabVisible && !isLiveUpdatesDisabled

  // The permission-missing case is NOT folded into the hide above: a viewer
  // who lacks `access Feedback` on a merchant that IS collecting still sees
  // the tab greyed out with a reason, exactly as before — hiding it would
  // read as "this feature doesn't exist here" rather than "you can't open
  // this". Only the business rule hides.
  const permissionDisabledReason = usePermissionReason(
    'Feedback',
    !canViewDetails
  )
  // A permission gap is the more actionable reason, so it wins over the disabled state.
  const feedbackDisabledReason =
    permissionDisabledReason ??
    (isLiveUpdatesDisabled
      ? liveUpdatesT('order_feedback_tabs.feedback_disabled')
      : undefined)

  const [hashTab, setHashTab] = useState<Tabs | null>(() =>
    readTabFromHash(history.location.hash)
  )
  useEffect(
    () =>
      history.listen(location => setHashTab(readTabFromHash(location.hash))),
    []
  )

  // Overview is the only tab that is always reachable; both others are hidden
  // in at least one state, or greyed out while Live Updates is off. A hidden
  // or greyed-out tab must also be unreachable by hash, otherwise `#settings`
  // selects a pane with no usable nav item to click back off of.
  const isTabReachable = (tab: Tabs): boolean => {
    if (tab === Tabs.FEEDBACK) return canViewFeedbackTab
    if (tab === Tabs.SETTINGS)
      return settingsTabVisible && !isLiveUpdatesDisabled
    return true
  }
  const requestedTab = hashTab ?? Tabs.OVERVIEW
  const activeTab = isTabReachable(requestedTab) ? requestedTab : Tabs.OVERVIEW

  const descriptionRef = useRef<HTMLDivElement>(null)
  // Scrolls past the title when the inbox shows (see useScrollInboxIntoView).
  // Gated on the flag: the legacy DetailsPane has no use for the extra room,
  // and merchants not yet on `recovery-ai-unified-feedback-inbox` must not be
  // auto-scrolled on a tab that hasn't changed. Also fires on a deep link
  // straight into #feedback, matching a click into the tab.
  useScrollInboxIntoView(
    descriptionRef,
    activeTab === Tabs.FEEDBACK && unifiedInboxEnabled
  )

  // Mirrors the pre-split page's guard: Tab.Content eager-mounts inactive
  // panes (transition={false}), so DetailsPane — and its order_ratings fetch
  // — only mounts while Feedback is actually active. The forbidden branch is
  // defense-in-depth for the (derive-not-seed) case where access is revoked
  // while the pane is mounted; the clamp above already keeps an unauthorized
  // viewer from landing here on a normal render.
  const renderFeedbackContent = () => {
    // The tab is greyed out and unreachable: stay empty rather than show the
    // permission-denied card.
    if (isLiveUpdatesDisabled) return null
    if (!canViewFeedbackTab) {
      return (
        <div className="text-center pt-xl">
          <img src={LockCircled} alt="" width={LOCK_ICON_WIDTH_PX} />
          <Text.Header4 bold color="grey90" className="mt-m mb-xs">
            {errorsT('forbidden_title')}
          </Text.Header4>
          <Text.BodyText3 color="grey70" tag="p">
            {errorsT('forbidden_message')}
          </Text.BodyText3>
        </div>
      )
    }
    if (activeTab !== Tabs.FEEDBACK) return null
    if (!unifiedInboxEnabled) return <DetailsPane />
    // `HostFlexShell` gives the inbox a flex column to fill — it is
    // viewport-bound rather than `calc(100vh - magic)` (docs/06-ux-rules.md
    // "Height model"). Scoped to this pane rather than wrapped around the
    // whole page so the Overview and Settings tabs keep their current
    // layout untouched.
    return (
      <HostFlexShell>
        <FeedbackInbox
          initialParams={{ kind: 'feedback', status: 'open' }}
          setupState={setupState}
          collecting={collecting}
          writtenFeedbackEnabled={writtenFeedbackEnabled}
        />
      </HostFlexShell>
    )
  }

  return (
    <>
      <Helmet>
        <title>{I18n.t(`titles./order_feedback#${activeTab}`)}</title>
      </Helmet>
      <PageHeader
        title={t('header.title')}
        description={t('header.description')}
        containerClassName="order-feedback-page"
        descriptionRef={descriptionRef}
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            if (tab && isTab(tab)) {
              history.replace({ ...history.location, hash: tab })
            }
          }}
        >
          {/* Three states leave Overview as the only tab. A one-item tab bar
              is the intended presentation there — confirmed with design
              against prototype v67 — so the bar is not suppressed. */}
          <TabNav ariaLabel={t('tabs.tablist_label')}>
            <TabItem state={Tabs.OVERVIEW} title={t('tabs.overview')} />
            {feedbackTabVisible && (
              <TabItem
                state={Tabs.FEEDBACK}
                disabled={!canViewDetails || isLiveUpdatesDisabled}
                disabledReason={feedbackDisabledReason}
                title={t('tabs.feedback')}
              />
            )}
            {settingsTabVisible && (
              <TabItem
                state={Tabs.SETTINGS}
                disabled={isLiveUpdatesDisabled}
                disabledReason={
                  isLiveUpdatesDisabled
                    ? liveUpdatesT('order_feedback_tabs.settings_disabled')
                    : undefined
                }
                title={t('tabs.settings')}
              />
            )}
          </TabNav>
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            {/* The banner is Overview-only: the other tabs are greyed out while
                Live Updates is off, so their panes never show. */}
            <TabPane state={Tabs.OVERVIEW}>
              <Container className="px-0" data-testid="tab-overview">
                {liveUpdatesState.isDisabled && (
                  <Container
                    className="mb-m px-0"
                    data-testid="order-feedback-disabled-banner"
                  >
                    <LiveUpdatesDisabledBanner
                      lead={liveUpdatesT('disabled_banner.order_feedback.lead')}
                      body={liveUpdatesT('disabled_banner.order_feedback.body')}
                      resumeText={liveUpdatesT(
                        'disabled_banner.order_feedback.resume'
                      )}
                      resume={
                        canOpenLiveUpdates
                          ? { kind: 'link', to: LIVE_UPDATES_PATH }
                          : undefined
                      }
                    />
                  </Container>
                )}
                <Overview isLiveUpdatesDisabled={isLiveUpdatesDisabled} />
              </Container>
            </TabPane>
            {/* A hidden tab drops its pane as well. Tab.Content eager-mounts
                inactive panes (transition={false}), so leaving the pane behind
                would mount — and let SettingsPane fetch — in exactly the
                states the design says have no Settings at all. */}
            {feedbackTabVisible && (
              // `paddingY="none"` only while the Unified Feedback Inbox
              // actually renders — it brings its own complete vertical-
              // spacing system (INBOX_PAGE_PADDING_PX). The flag-off
              // fallback below (`DetailsPane`) has no spacing of its own and
              // still wants the default `py-xl` padding this pane normally
              // supplies.
              <TabPane
                state={Tabs.FEEDBACK}
                paddingY={unifiedInboxEnabled ? 'none' : 'xl'}
              >
                <Container className="px-0" data-testid="tab-feedback">
                  {renderFeedbackContent()}
                </Container>
              </TabPane>
            )}
            {settingsTabVisible && (
              <TabPane state={Tabs.SETTINGS}>
                <Container className="px-0" data-testid="tab-settings">
                  {/* Tab.Content eager-mounts inactive panes, and SettingsPane
                      listens on the shared history singleton, so mount it only
                      while Settings is actually being viewed. */}
                  {activeTab === Tabs.SETTINGS && (
                    <SettingsPane hashPrefix={Tabs.SETTINGS} />
                  )}
                </Container>
              </TabPane>
            )}
          </Tab.Content>
        </Tab.Container>
      </PageHeader>
    </>
  )
}

export default OrderFeedback
